import { Query } from '@apollo/client/react/components';
import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { identifyUser } from '../../lib/analytics/identifyUser';
import { billingAccountQuery } from '../../graphql-operations';
import { gqlTypes } from '../../types';
import useCheckoutPaths from '../../hooks/useCheckoutPaths';
import * as events from '../../lib/analytics/events';
import ThankYouLocationState from '../../models/ThankYouLocationState';
import * as types from '../../types';
import { SUPPLEMENT_PREFIXES } from '../../views/ManageSubscription/Supplements/SplashPage';
import { useSeries3Context } from '../../contexts/Series3Context';
import { supplementsProductIsSupportedInCountry, useLocale } from '../../contexts/LocaleContext';

interface IFinishedRedirectProps {
  cart: types.Cart;
  cartPricing: types.CartPricing;
  isGWP: boolean;
  isReturningCustomer: boolean;
  orderId: string;
  applePayUserSession?: types.Session;
}

export default function FinishedRedirect({
  cart,
  cartPricing,
  orderId,
  isGWP,
  isReturningCustomer,
  applePayUserSession,
}: IFinishedRedirectProps) {
  const session = useSelector((state: types.AppState) => state.session);
  const checkoutPaths = useCheckoutPaths();
  const supplementInCart = Object.values(cart.cartItems).some((item) =>
    SUPPLEMENT_PREFIXES.some((prefix) => item.lineItem.sku.toUpperCase().startsWith(prefix)),
  );
  const { isVerizon } = useSeries3Context();
  const { country, currency } = useLocale();
  const showSupplementsUpsell = !supplementInCart && !isVerizon && supplementsProductIsSupportedInCountry(country);

  const createRedirect = (state: ThankYouLocationState) => {
    return (
      <Redirect
        to={{
          pathname: showSupplementsUpsell ? checkoutPaths.Supplements : checkoutPaths.ThankYou,
          state,
        }}
      />
    );
  };

  // This is a bit janky of a way to handle errors. Create empty information to display on the thank you page.
  // Technically the user has completed their purchase at this point - there was no error there, so displaying
  // the thank you page seems less confusing than displaying an error where they might not be sure if the
  // purchase went through.
  // This should be an extreme edge case, since at this point we should definitely have a billing account
  // as we just made a purchase.
  const createErrorRedirect = () =>
    createRedirect({
      orderId,
      orderedCart: cart,
      orderedCartPricing: cartPricing,
      applePayUserSession,
    });

  return (
    <Query<gqlTypes.billingAccount>
      query={billingAccountQuery}
      fetchPolicy="network-only"
      onCompleted={(data) => {
        const billingAccount = data.currentUser.billingAccount;
        if (billingAccount?.billingInfo && orderId) {
          const { id: userId, firstName, lastName } = data.currentUser;
          const { billingInfo, address } = billingAccount;

          // Identify the user with their information before tracking the order completed event
          identifyUser(userId, {
            email: session?.email,
            name: {
              firstName,
              lastName: lastName ?? undefined,
            },
            billingInfo,
            shippingAddress: address,
          });

          // Apple Pay completes shipping, payment, and purchase in a single gesture.
          // Fire the intermediate funnel events so the FB pixel sees the full funnel.
          events.shipping.continue(cart.cartItems, currency);
          events.payment.continue(cart.cartItems, currency);

          events.orderCompleted(cart, cartPricing, orderId, address ?? undefined, billingInfo, {
            isReturningCustomer,
            isGWP,
            currency,
          });
        }
      }}
    >
      {({ data, loading, error }) => {
        if (loading) {
          return null;
        }
        if (error || !data) {
          return createErrorRedirect();
        }

        const info: ThankYouLocationState = {
          orderId,
          orderedCart: cart,
          orderedCartPricing: cartPricing,
          applePayUserSession,
        };

        return createRedirect(info);
      }}
    </Query>
  );
}
